// userManagement.js — the admin's staff roster, on the 0012 rails.
//
// The pre-rewrite version of this file could not actually manage accounts: the
// anon-key client cannot mint or modify auth users, so "create" showed an
// alert() telling the cashier to SIGN THEMSELVES UP, and "edit" updated
// public.users while the login identity (auth.users email/password) stayed
// frozen — which read, correctly, as « je ne peux pas modifier les infos ».
// It also called an `update_user_metadata` RPC that never existed, and bound
// its row buttons with `onclick="…"` attributes (audit M7).
//
// Now every mutation goes through the three admin-gated SECURITY DEFINER RPCs
// from migration 0012 — the server owns the guards (last admin can be neither
// demoted nor deleted, no self-delete, ledger-named accounts protected by the
// real foreign keys) and raises them in French, so this module mostly just
// relays `error.message`:
//
//   admin_create_user(p_email, p_password, p_role)  -> a WORKING login account
//   admin_update_user(p_user_id, p_email, p_password, p_role)  null = unchanged
//   admin_delete_user(p_user_id)
//
// Row actions are DELEGATED listeners on the tbody with data-* attributes —
// no user-supplied text is ever interpolated into an attribute (audit M7),
// and everything painted into innerHTML goes through escapeHtml.

import { supabase } from './supabaseClient.js';
import { showStatus } from './utils.js';

/**
 * Escape text destined for an `innerHTML` template (audit M7).
 * @param {*} value
 * @returns {string}
 */
function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

const ROLE_LABELS = {
    admin: 'Administrateur',
    cashier_a: 'Caissier A (Services)',
    cashier_b: 'Caissier B (Pharmacie)'
};

export const UserManagement = {
    elements: {},
    /** @type {object[]} the roster, as last loaded */
    users: [],
    /** The signed-in admin's own id — their row hides the delete button. */
    currentUserId: null,

    init() {
        this.elements = {
            modal: document.getElementById('user-management-modal'),
            manageUsersBtn: document.getElementById('manage-users-btn'),
            closeModalBtn: document.getElementById('close-user-modal'),
            addUserBtn: document.getElementById('add-user-btn'),
            userForm: document.getElementById('user-form'),
            userFormSection: document.getElementById('user-form-section'),
            userFormTitle: document.getElementById('user-form-title'),
            cancelFormBtn: document.getElementById('cancel-user-form'),
            usersTableBody: document.getElementById('users-table-body'),
            userIdInput: document.getElementById('user-id'),
            userEmailInput: document.getElementById('user-email-input'),
            userPasswordInput: document.getElementById('user-password-input'),
            userRoleSelect: document.getElementById('user-role-select')
        };

        this.setupEventListeners();
        console.log('UserManagement module initialized');
    },

    setupEventListeners() {
        this.elements.manageUsersBtn?.addEventListener('click', () => this.openModal());
        this.elements.closeModalBtn?.addEventListener('click', () => this.closeModal());
        this.elements.modal?.addEventListener('click', (e) => {
            if (e.target === this.elements.modal) this.closeModal();
        });

        this.elements.addUserBtn?.addEventListener('click', () => this.showUserForm());
        this.elements.cancelFormBtn?.addEventListener('click', () => this.hideUserForm());
        this.elements.userForm?.addEventListener('submit', (e) => this.handleFormSubmit(e));

        // Delegated row actions (audit M7): the buttons carry ids, never text.
        this.elements.usersTableBody?.addEventListener('click', (e) => {
            const button = e.target instanceof Element ? e.target.closest('button[data-action]') : null;
            if (!button) return;
            const id = button.getAttribute('data-user-id');
            switch (button.getAttribute('data-action')) {
                case 'edit': this.editUser(id); break;
                case 'delete': void this.deleteUser(id); break;
            }
        });
    },

    async openModal() {
        this.elements.modal?.classList.remove('hidden');
        await this.loadUsers();
    },

    closeModal() {
        this.elements.modal?.classList.add('hidden');
        this.hideUserForm();
    },

    /** @param {object|null} user null = create mode */
    showUserForm(user = null) {
        this.elements.userFormSection?.classList.remove('hidden');

        if (user) {
            this.elements.userFormTitle.textContent = 'Modifier l\'utilisateur';
            this.elements.userIdInput.value = user.id;
            this.elements.userEmailInput.value = user.email;
            this.elements.userPasswordInput.value = '';
            this.elements.userPasswordInput.required = false;
            this.elements.userPasswordInput.placeholder = 'Laisser vide pour conserver le mot de passe actuel';
            this.elements.userRoleSelect.value = user.role;
        } else {
            this.elements.userFormTitle.textContent = 'Nouvel utilisateur';
            this.elements.userForm.reset();
            this.elements.userIdInput.value = '';
            // A new account without a password cannot log in — required, and
            // the 0012 function refuses under 8 characters anyway.
            this.elements.userPasswordInput.required = true;
            this.elements.userPasswordInput.placeholder = 'Mot de passe (8 caractères minimum)';
        }
    },

    hideUserForm() {
        this.elements.userFormSection?.classList.add('hidden');
        this.elements.userForm?.reset();
    },

    async loadUsers() {
        this.elements.usersTableBody.innerHTML =
            '<tr><td colspan="4" class="px-4 py-3 text-center text-gray-500">Chargement des comptes…</td></tr>';

        try {
            if (!this.currentUserId) {
                const { data: authData } = await supabase.auth.getUser();
                this.currentUserId = authData?.user?.id || null;
            }

            const { data: users, error } = await supabase
                .from('users')
                .select('*')
                .order('email', { ascending: true });

            if (error) throw error;

            this.users = users || [];
            if (this.users.length > 0) {
                this.displayUsers(this.users);
            } else {
                this.elements.usersTableBody.innerHTML =
                    '<tr><td colspan="4" class="px-4 py-3 text-center text-gray-500">Aucun compte</td></tr>';
            }
        } catch (error) {
            console.error('Erreur au chargement des comptes:', error);
            showStatus('Échec du chargement des comptes : ' + error.message, true);
            this.elements.usersTableBody.innerHTML =
                '<tr><td colspan="4" class="px-4 py-3 text-center text-red-500">Erreur de chargement</td></tr>';
        }
    },

    displayUsers(users) {
        this.elements.usersTableBody.innerHTML = '';

        users.forEach(user => {
            const row = document.createElement('tr');
            row.className = 'border-b hover:bg-gray-50';

            const createdDate = user.created_at
                ? new Date(user.created_at).toLocaleDateString('fr-FR')
                : '—';

            const roleColor = user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                            user.role === 'cashier_a' ? 'bg-blue-100 text-blue-800' :
                            'bg-green-100 text-green-800';

            // Own row: no delete button — the server refuses a self-delete
            // anyway (0012); offering the control would only surface the error.
            const isSelf = user.id === this.currentUserId;

            row.innerHTML = `
                <td class="px-4 py-3 text-sm">${escapeHtml(user.email)}</td>
                <td class="px-4 py-3 text-sm">
                    <span class="px-2 py-1 text-xs font-semibold rounded-full ${roleColor}">
                        ${escapeHtml(ROLE_LABELS[user.role] || user.role)}
                    </span>
                </td>
                <td class="px-4 py-3 text-sm text-gray-600">${escapeHtml(createdDate)}</td>
                <td class="px-4 py-3 text-center"></td>
            `;

            const actions = row.lastElementChild;
            const editBtn = document.createElement('button');
            editBtn.type = 'button';
            editBtn.className = 'btn-edit';
            editBtn.textContent = 'Modifier';
            editBtn.setAttribute('data-action', 'edit');
            editBtn.setAttribute('data-user-id', user.id);
            actions.appendChild(editBtn);

            if (isSelf) {
                const note = document.createElement('span');
                note.className = 'text-xs text-gray-500 ml-2';
                note.textContent = '(votre compte)';
                actions.appendChild(note);
            } else {
                const deleteBtn = document.createElement('button');
                deleteBtn.type = 'button';
                deleteBtn.className = 'btn-delete';
                deleteBtn.textContent = 'Supprimer';
                deleteBtn.setAttribute('data-action', 'delete');
                deleteBtn.setAttribute('data-user-id', user.id);
                actions.appendChild(deleteBtn);
            }

            this.elements.usersTableBody.appendChild(row);
        });
    },

    async handleFormSubmit(e) {
        e.preventDefault();

        const userId = this.elements.userIdInput.value;
        const email = this.elements.userEmailInput.value.trim();
        const password = this.elements.userPasswordInput.value;
        const role = this.elements.userRoleSelect.value;

        if (!role) {
            showStatus('Choisissez un rôle.', true);
            return;
        }

        if (userId) {
            await this.updateUser(userId, email, role, password);
        } else {
            await this.createUser(email, password, role);
        }
    },

    async createUser(email, password, role) {
        if (!password || password.length < 8) {
            showStatus('Mot de passe trop court : 8 caractères minimum.', true);
            return;
        }
        try {
            showStatus('Création du compte…', false);

            const { error } = await supabase.rpc('admin_create_user', {
                p_email: email,
                p_password: password,
                p_role: role
            });
            if (error) throw error;

            showStatus(`Compte créé : ${email} peut se connecter dès maintenant.`, false);
            this.hideUserForm();
            await this.loadUsers();
        } catch (error) {
            console.error('Erreur à la création du compte:', error);
            // The 0012 messages are already French — relay them as-is.
            showStatus('Échec de la création : ' + error.message, true);
        }
    },

    async updateUser(userId, email, role, password) {
        try {
            showStatus('Mise à jour du compte…', false);

            const { error } = await supabase.rpc('admin_update_user', {
                p_user_id: userId,
                p_email: email,
                p_password: password || null,   // vide = mot de passe conservé
                p_role: role
            });
            if (error) throw error;

            showStatus('Compte mis à jour.', false);
            this.hideUserForm();
            await this.loadUsers();
        } catch (error) {
            console.error('Erreur à la mise à jour du compte:', error);
            showStatus('Échec de la mise à jour : ' + error.message, true);
        }
    },

    editUser(userId) {
        const user = this.users.find(u => u.id === userId);
        if (!user) {
            showStatus('Compte introuvable — rechargez la liste.', true);
            return;
        }
        this.showUserForm(user);
    },

    async deleteUser(userId) {
        const user = this.users.find(u => u.id === userId);
        if (!user) return;

        if (!confirm(`Supprimer le compte ${user.email} ?\n\nUn compte nommé sur des encaissements ne peut pas être supprimé — changez plutôt son rôle ou son mot de passe.`)) {
            return;
        }

        try {
            showStatus('Suppression du compte…', false);

            const { error } = await supabase.rpc('admin_delete_user', { p_user_id: userId });
            if (error) throw error;

            showStatus(`Compte ${user.email} supprimé.`, false);
            await this.loadUsers();
        } catch (error) {
            console.error('Erreur à la suppression du compte:', error);
            showStatus('Échec de la suppression : ' + error.message, true);
        }
    }
};

// Kept for anything that still reaches the module through the global (app.js
// exposes modules on window); the row buttons themselves no longer need it.
window.UserManagement = UserManagement;
